import React, { useState } from 'react'
import {
	Card,
	CardHeader,
	CardPreview,
	Text,
	Button,
	makeStyles,
	shorthands,
	tokens,
	Badge,
	Divider
} from '@fluentui/react-components'
import { Filter24Regular, GridDots24Regular, List24Regular, ArrowSort24Regular } from '@fluentui/react-icons'
import type { Skill } from './types'
import { skillsMatrixContent } from './content'
import { useCommonStyles } from '../common/styles'
import AnimatedProgressBar from './AnimatedProgressBar'
import { useSkillsFilter } from '../../contexts/SkillsFilterContext'

const useStyles = makeStyles({
	header: {
		textAlign: 'center',
		...shorthands.margin('0', '0', '2em', '0')
	},
	filterBar: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		...shorthands.gap('0.5em'),
		...shorthands.padding('0.75em'),
		backgroundColor: tokens.colorNeutralBackground2,
		...shorthands.borderRadius(tokens.borderRadiusMedium),
		'@media (min-width: 768px)': {
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
			...shorthands.gap('1em'),
			...shorthands.padding('1em')
		}
	},
	filterButtons: {
		display: 'flex',
		alignItems: 'center',
		...shorthands.gap('0.25em'),
		flexWrap: 'wrap',
		'@media (min-width: 768px)': {
			...shorthands.gap('0.5em')
		}
	},
	categoryGrid: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		...shorthands.gap('0.75em'),
		'@media (min-width: 480px)': {
			gridTemplateColumns: 'repeat(auto-fit, minmax(15.6rem, 1fr))',
			...shorthands.gap('1em')
		},
		'@media (min-width: 768px)': {
			gridTemplateColumns: 'repeat(auto-fit, minmax(21.9rem, 1fr))',
			...shorthands.gap('1.5em')
		}
	},
	categoryCard: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%'
	},
	skillItem: {
		display: 'flex',
		flexDirection: 'column',
		...shorthands.gap('0.5em'),
		...shorthands.padding('0.75em'),
		...shorthands.borderRadius(tokens.borderRadiusMedium),
		backgroundColor: tokens.colorNeutralBackground1,
		':hover': {
			backgroundColor: tokens.colorNeutralBackground1Hover,
			transform: 'translateY(-0.125em)',
			boxShadow: tokens.shadow8
		},
		transition: 'all 0.2s ease'
	},
	skillHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center'
	},
	skillProgress: {
		display: 'flex',
		flexDirection: 'column',
		...shorthands.gap('0.25em')
	},
	certificationBadges: {
		display: 'flex',
		flexWrap: 'wrap',
		...shorthands.gap('0.25em'),
		...shorthands.margin('0.5em', '0', '0', '0')
	},
	listView: {
		display: 'flex',
		flexDirection: 'column',
		...shorthands.gap('1em'),
		paddingTop: tokens.spacingVerticalM
	},
	listCategory: {
		...shorthands.margin('0', '0', '1.5em', '0')
	},
	listCategoryTitle: {
		...shorthands.margin('0', '0', '0.75em', '0'),
		...shorthands.padding('0', '0', '0.5em', tokens.spacingHorizontalM),
		borderBottom: `2px solid ${tokens.colorNeutralStroke2}`,
		color: tokens.colorNeutralForeground1
	},
	listSkillsGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(20rem, 1fr))',
		...shorthands.gap('0.75em'),
		paddingLeft: tokens.spacingHorizontalM,
		'@media (max-width: 768px)': {
			gridTemplateColumns: '1fr'
		}
	},
	gridTransition: {
		transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
		transform: 'scale(1)',
		opacity: 1,
		'&.filtering': {
			transform: 'scale(0.95)',
			opacity: 0.7
		}
	},
	sortControls: {
		display: 'flex',
		...shorthands.gap('0.25em'),
		alignItems: 'center'
	},
	cardContent: {
		padding: '0.5rem',
		margin: '0',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		justifyContent: 'space-evenly',
		...shorthands.gap('0.5rem')
	},
	cardPreview: {
		padding: '0 !important',
		margin: '0 !important',
		display: 'flex',
		flexDirection: 'column',
		height: '100%'
	},
	gridCategoryTitle: {
		color: tokens.colorNeutralForeground1
	}
})

type SortOption = 'level' | 'experience' | 'name' | 'default'

export const SkillsMatrix: React.FC = () => {
	const styles = useStyles()
	const commonStyles = useCommonStyles()
	// Category filter is shared app state — the architecture diagram
	// pre-filters the matrix when a node is selected.
	const { category: selectedCategory, setCategory: setSelectedCategory } = useSkillsFilter()
	const [sortBy, setSortBy] = useState<SortOption>('default')
	const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

	const categories = ['all', ...skillsMatrixContent.categories.map(cat => cat.name)]

	const currentYear = new Date().getFullYear()
	const getYearsExperience = (skill: Skill) => currentYear - skill.sinceYear

	const sortSkillsInCategory = (skills: Skill[]): Skill[] => {
		if (sortBy === 'default') return skills

		return [...skills].sort((a, b) => {
			switch (sortBy) {
				case 'level':
					return b.level - a.level // Highest first
				case 'experience':
					return a.sinceYear - b.sinceYear // Lower sinceYear = more experience
				case 'name':
					return a.name.localeCompare(b.name) // Alphabetical
				default:
					return 0
			}
		})
	}

	const filteredData = selectedCategory === 'all'
		? skillsMatrixContent.categories.map(cat => ({
			...cat,
			skills: sortSkillsInCategory(cat.skills)
		}))
		: skillsMatrixContent.categories
			.filter(cat => cat.name === selectedCategory)
			.map(cat => ({
				...cat,
				skills: sortSkillsInCategory(cat.skills)
			}))

	const getSkillLevelText = (level: number): string => {
		if (level >= 90) return 'Expert'
		if (level >= 75) return 'Advanced'
		if (level >= 60) return 'Intermediate'
		return 'Beginner'
	}

	const getSkillLevelColor = (level: number): string => {
		if (level >= 90) return tokens.colorPaletteGreenForeground2
		if (level >= 75) return tokens.colorPaletteBlueForeground2
		if (level >= 60) return tokens.colorPaletteYellowForeground2
		return tokens.colorPaletteRedForeground2
	}

	return (
		<div className={commonStyles.container}>
			<div className={styles.filterBar}>
				<div className={styles.filterButtons}>
					<Filter24Regular />
					{categories.map(category => (
						<Button
							key={category}
							appearance={selectedCategory === category ? 'primary' : 'secondary'}
							size="small"
							onClick={() => setSelectedCategory(category)}
							aria-label={`Filter by ${category} category`}
							aria-pressed={selectedCategory === category}
							style={{
								fontSize: '0.7rem',
								minWidth: 'auto',
								padding: '0.125em 0.375em',
								whiteSpace: 'nowrap'
							}}
						>
							{category === 'all' ? 'All' : category
								.replace('SharePoint Framework (SPFx)', 'SPFx')
								.replace('Microsoft Teams Development', 'Teams')
								.replace('Development Technologies', 'Dev Tech')
								.replace('Azure Services', 'Azure')
								.replace('Infrastructure & DevOps', 'Infra & DevOps')
								.replace('AI & Agentic Development', 'AI')}
						</Button>
					))}
				</div>
				<div className={styles.sortControls}>
					<Button
						appearance="subtle"
						icon={<ArrowSort24Regular />}
						onClick={() => {
							const sortOptions: SortOption[] = ['default', 'level', 'experience', 'name']
							const currentIndex = sortOptions.indexOf(sortBy)
							const nextIndex = (currentIndex + 1) % sortOptions.length
							setSortBy(sortOptions[nextIndex])
						}}
						aria-label={`Sort skills by ${sortBy === 'default' ? 'default order' : sortBy}. Click to cycle through sort options.`}
						size="small"
						style={{
							fontSize: '0.7rem',
							minWidth: 'auto',
							padding: '0.125em 0.375em'
						}}
					>
						Sort: {sortBy === 'default' ? 'Default' : sortBy === 'level' ? 'Level' : sortBy === 'experience' ? 'Exp.' : 'Name'}
					</Button>
					<Button
						appearance="subtle"
						icon={viewMode === 'grid' ? <List24Regular /> : <GridDots24Regular />}
						onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
						aria-label={`Switch to ${viewMode === 'grid' ? 'list' : 'grid'} view`}
						aria-pressed={false}
						size="small"
						style={{
							fontSize: '0.7rem',
							minWidth: 'auto',
							padding: '0.125em 0.375em'
						}}
					>
						{viewMode === 'grid' ? 'List' : 'Grid'}
					</Button>
				</div>
			</div>

			{viewMode === 'grid' ? (
				<div className={`${styles.categoryGrid} ${styles.gridTransition}`}>
					{filteredData.map(category => (
						<Card key={category.name} className={styles.categoryCard}>
							<CardHeader
								header={
									<div>
										<Text weight="semibold" size={500} className={styles.gridCategoryTitle}>
											{category.name}
										</Text>
										&nbsp;
										<Badge
											color="brand"
											appearance="filled"
											size="small"
										>
											{category.skills.length} skills
										</Badge>
									</div>
								}
							/>
							<Divider />
							<CardPreview className={styles.cardPreview}>
								<div className={styles.cardContent}>
									{category.skills.map(skill => (
										<div key={skill.id} className={styles.skillItem}>
											<div className={styles.skillHeader}>
												<Text weight="semibold">{skill.name}</Text>
												<Badge
													color="informative"
													appearance="outline"
													size="small"
												>
													{getYearsExperience(skill)}y exp
												</Badge>
											</div>

											<Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
												{skill.description}
											</Text>

											<div className={styles.skillProgress}>
												<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
													<Text size={200}>Proficiency</Text>
													<Text
														size={200}
														weight="semibold"
														style={{ color: getSkillLevelColor(skill.level) }}
													>
														{getSkillLevelText(skill.level)} ({skill.level}%)
													</Text>
												</div>
												<AnimatedProgressBar
													value={skill.level / 100}
													color={skill.level >= 90 ? 'success' : skill.level >= 75 ? 'brand' : 'warning'}
												/>
											</div>

											{skill.certifications && skill.certifications.length > 0 && (
												<div className={styles.certificationBadges}>
													{skill.certifications.map(cert => (
														<Badge
															key={cert}
															color="success"
															appearance="filled"
															size="extra-small"
														>
															{cert}
														</Badge>
													))}
												</div>
											)}
										</div>
									))}
								</div>
							</CardPreview>
						</Card>
					))}
				</div>
			) : (
				<div className={styles.listView}>
					{filteredData.map(category => (
						<div key={category.name} className={styles.listCategory}>
							<div className={styles.listCategoryTitle}>
								<Text as="h2" size={500} weight="semibold">
									{category.name}
								</Text>
								<Badge
									color="brand"
									appearance="filled"
									size="small"
									style={{ marginLeft: '1em' }}
								>
									{category.skills.length} skills
								</Badge>
							</div>
							<div className={styles.listSkillsGrid}>
								{category.skills.map(skill => (
									<Card key={skill.id} className={styles.cardContent}>
										<div className={styles.skillItem}>
											<div className={styles.skillHeader}>
												<Text weight="semibold">{skill.name}</Text>
												<Badge
													color="informative"
													appearance="outline"
													size="small"
												>
													{getYearsExperience(skill)}y exp
												</Badge>
											</div>

											<Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
												{skill.description}
											</Text>

											<div className={styles.skillProgress}>
												<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
													<Text size={200}>Proficiency</Text>
													<Text
														size={200}
														weight="semibold"
														style={{ color: getSkillLevelColor(skill.level) }}
													>
														{getSkillLevelText(skill.level)} ({skill.level}%)
													</Text>
												</div>
												<AnimatedProgressBar
													value={skill.level / 100}
													color={skill.level >= 90 ? 'success' : skill.level >= 75 ? 'brand' : 'warning'}
												/>
											</div>

											{skill.certifications && skill.certifications.length > 0 && (
												<div className={styles.certificationBadges}>
													{skill.certifications.map(cert => (
														<Badge
															key={cert}
															color="success"
															appearance="filled"
															size="extra-small"
														>
															{cert}
														</Badge>
													))}
												</div>
											)}
										</div>
									</Card>
								))}
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

export default SkillsMatrix